//--------------------------------------------------------------------------------------------------Imports
import { Button, Checkbox, Col, Input, Row, Select, Typography } from "antd";
import type { ConfigurableService, ResponsePackageItem } from "../../../types/common";
import { StyleSheet } from "../../../StyleSheet";
import { useCallback, useEffect, useMemo, useState } from "react";
import MarkdownPreviewer from "../../../common/MarkdownPreviewer";
import { useDispatch, useSelector } from "react-redux";
import { getGeneralData } from "../../../stores/slices/generalDataSlice";
import { hireMegaMovil } from "../../../methods/Methods";
import { AppDispatch } from "../../../stores/store";
import { clearAlert, setAlert, showAlert } from "../../../stores/slices/alertSlice";
import Loading from "../../../components/Loading";
import { clearTrackingMegamovilData, fillMegaMovilTrackingSlice } from '../../../stores/slices/megamovilTrackingSlice';
import { getCurpValidationError, getRfcValidationError } from "../../../common/functions";
import QuantitySelector from "./QuantitySelector";
import LineTypeSelector from "./LineTypeSelector";
import { clearAlertConfirmation, setAlertConfirmation, showAlertConfirmation } from "../../../stores/slices/alertConfirmationSlice";
import { buildHireMegaMovilPayload } from "./buildHireMegaMovilPayload";
import { getHireMegaMovilConfig } from "./hireMegaMovilConfig";
import { getHireMegaMovilValidationError } from "./hireMegaMovilValidation";

const { Text } = Typography;

type LineType = "SIM" | "ESIM";

//-------------------------------------------------------------------------------------------------- Interface
interface HireMegaMovilServicesComponentProps {
    packageSrv: ResponsePackageItem;
};

//-------------------------------------------------------------------------------------------------- Component
const HireMegaMovilServicesComponent = (props: HireMegaMovilServicesComponentProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const { selectedContract } = useSelector(getGeneralData);
    // Control
    const [state, setState] = useState<number>(0);
    // Data
    const [clientId, setClientId] = useState<string>("");
    const { packageSrv } = props;
    const [checked, setChecked] = useState<boolean>(false);
    const [loadingHireMegamovil, setLoadingHireMegamovil] = useState<boolean>(false);
    const [curp, setCurp] = useState('');
    const [curpError, setCurpError] = useState('');
    const [rfc, setRfc] = useState('');
    const [rfcError, setRfcError] = useState('');
    const [socialReasonType, setSocialReasonType] = useState<string>('');
    const [lineTypeSelections, setLineTypeSelections] = useState<LineType[]>([]);

    const businessTypes = [
        { value: 'PM', label: 'Persona moral' },
        { value: 'AE', label: 'Persona física con actividad emp' },
        { value: 'EG', label: 'Entidad gubernamental' },
    ];
    const [cantidad, setCantidad] = useState("");

    const confServiceData = useSelector(
        (state: any) => (state.configurableService.dataList ?? []) as ConfigurableService[]
    );

    const {
        limitToOne,
        showCurp,
        showBusinessFields,
        max,
        showLineTypeSelector,
        familyLineDefinitions,
    } = useMemo(
        () => getHireMegaMovilConfig(packageSrv, confServiceData),
        [packageSrv, confServiceData],
    );

    const hasFamilyLineDefinitions = familyLineDefinitions.length > 0;
    const isFamilyPackage = packageSrv.packageType === "FAMILIAR";
    const quantitySelected = Number.parseInt(cantidad, 10);
    const quantityToRender = Number.isNaN(quantitySelected) ? 0 : Math.max(quantitySelected, 0);
    const eSimCount = lineTypeSelections.filter((lineType) => lineType === "ESIM").length;
    const simCount = lineTypeSelections.length - eSimCount;
    const confirmationTotalLines = isFamilyPackage
        ? (familyLineDefinitions.length > 0 ? familyLineDefinitions.length : 1)
        : (lineTypeSelections.length > 0 ? lineTypeSelections.length : quantityToRender);
    const confirmationSummary = `${confirmationTotalLines} ${confirmationTotalLines === 1 ? 'línea' : 'líneas'} (${simCount} SIM y ${eSimCount} eSIM)`;

    const handleFinalizeAlert = useCallback(() => {
        dispatch(showAlert(false));
        dispatch(clearAlert());
        dispatch(fillMegaMovilTrackingSlice({ clientId }));
    }, [dispatch, clientId]);

    const handleLineTypeChange = useCallback((lineIndex: number, lineType: LineType) => {
        setLineTypeSelections((prev) => {
            const next = [...prev];
            next[lineIndex] = lineType;
            return next;
        });
    }, []);

    const handleCurpChange = (text: string) => {
        const upperCaseText = text.toUpperCase();
        setCurp(upperCaseText);

        setCurpError(getCurpValidationError(upperCaseText));
    };

    const handleRfcChange = (text: string) => {
        const upperCaseText = text.toUpperCase();
        setRfc(upperCaseText);
        setRfcError(getRfcValidationError(upperCaseText));
    };

    useEffect(() => {
        if (limitToOne) {
            setCantidad("1");
        }
    }, [limitToOne]);

    useEffect(() => {
        if (!showLineTypeSelector) {
            setLineTypeSelections([]);
            return;
        }

        const targetLength = hasFamilyLineDefinitions
            ? familyLineDefinitions.length
            : (limitToOne ? 1 : quantityToRender);

        if (targetLength <= 0) {
            setLineTypeSelections([]);
            return;
        }

        setLineTypeSelections((prev) => {
            if (prev.length === targetLength) {
                return prev;
            }

            const next = [...prev];
            if (next.length < targetLength) {
                while (next.length < targetLength) {
                    next.push("SIM");
                }
            } else {
                next.length = targetLength;
            }

            return next;
        });
    }, [
        showLineTypeSelector,
        hasFamilyLineDefinitions,
        familyLineDefinitions.length,
        limitToOne,
        quantityToRender,
    ]);

    const sendMegaMovilConfirm = useCallback(async () => {
        dispatch(showAlertConfirmation(false));
        dispatch(clearAlertConfirmation());

        let message = "";
        let show = false;
        let typeAlert: "INFO" | "SUCCESS" | "ERROR" = "INFO";

        let packageMegaM;
        try {
            packageMegaM = buildHireMegaMovilPayload({
                item: packageSrv,
                cantidad,
                curp: showCurp ? curp : "",
                rfc: showBusinessFields ? rfc : "",
                tipificacion: socialReasonType,
                lineTypeSelections,
                familyLineDefinitions,
            });
        } catch (error) {
            const messageError = error instanceof Error
                ? error.message
                : "No fue posible preparar la solicitud de contratación.";

            dispatch(setAlert({
                show: true,
                title: "¡Estimado Usuario!",
                message: messageError,
                type: "INFO",
                onFinalize: handleFinalizeAlert,
            }));
            return;
        }

        setLoadingHireMegamovil(true);
        await hireMegaMovil(packageMegaM, clientId)
            .then((response) => {
                const resp = response.data.data[0];
                if (resp) {
                    if (resp?.status === true) {
                        message = resp.message;
                        typeAlert = "SUCCESS";
                        setState(2);
                        dispatch(clearTrackingMegamovilData());
                    } else if (resp?.status === false) {
                        if (resp.error === "3" || resp.error === "4") {
                            typeAlert = "INFO";
                        } else {
                            typeAlert = "ERROR";
                        }
                        message = resp.message;
                        setState(0);
                    }
                    show = true;
                    setLoadingHireMegamovil(false);
                }
            })
            .catch(() => {
                typeAlert = "ERROR";
                message = "Ha ocurrido un error, intentelo más tarde.";
                show = true;
                setState(0);
                setLoadingHireMegamovil(false);
            });

        dispatch(setAlert({
            show: show,
            title: "¡Estimado Usuario!",
            message: message,
            type: typeAlert,
            onFinalize: handleFinalizeAlert
        }));

    }, [
        packageSrv,
        clientId,
        dispatch,
        handleFinalizeAlert,
        curp,
        cantidad,
        rfc,
        showBusinessFields,
        showCurp,
        socialReasonType,
        lineTypeSelections,
        familyLineDefinitions,
    ]);

    const onPressBtnSecondary = useCallback(() => {
        dispatch(showAlertConfirmation(false));
        dispatch(clearAlertConfirmation());
    }, [dispatch]);

    const handleConfirmPurchase = useCallback(() => {
        const validationError = getHireMegaMovilValidationError({
            showCurp,
            curp,
            showBusinessFields,
            socialReasonType,
            rfc,
            cantidad,
        });

        if (validationError) {
            dispatch(setAlert({
                show: true,
                title: "¡Estimado Usuario!",
                message: validationError,
                type: "INFO",
                onFinalize: handleFinalizeAlert
            }));
            return;
        }

        const defaultQuantitySummary = `${confirmationTotalLines} ${confirmationTotalLines > 1 ? "líneas" : "línea"}`;
        const confirmationSubText = showLineTypeSelector
            ? confirmationSummary
            : defaultQuantitySummary;

        dispatch(setAlertConfirmation({
            onPressBtnPrimary: sendMegaMovilConfirm,
            onPressBtnSecondary: onPressBtnSecondary,
            title: "Confirmación de Contratación",
            message: `¿Está seguro de contratar`,
            subText: confirmationSubText,
            subMessage: "Mega móvil?",
            show: true
        }));
    }, [
        cantidad,
        curp,
        dispatch,
        handleFinalizeAlert,
        onPressBtnSecondary,
        sendMegaMovilConfirm,
        rfc,
        showBusinessFields,
        showCurp,
        socialReasonType,
        showLineTypeSelector,
        confirmationTotalLines,
        confirmationSummary,
    ]);


    useEffect(() => {
        if (selectedContract) {
            setClientId(selectedContract.sucursal + selectedContract.suscriptor);
        }
    }, [selectedContract]);

    const renderStateZero = () => (
        <Row>
            <Col span={18} style={styles.mdContainer}>
                <MarkdownPreviewer
                    h1Style={{
                        fontSize: 15,
                        color: "#000000",
                        fontWeight: 700
                    }}
                    h2Style={{
                        fontSize: 12,
                        color: "#707070",
                        fontWeight: 500
                    }}
                    h3Style={{
                        fontSize: 12,
                        color: "#000000",
                        margin: 0,
                        padding: 0,
                        fontWeight: 500
                    }}
                    h4Style={{
                        fontSize: 12,
                        color: "#000000",
                        margin: 0,
                        padding: 0,
                        fontWeight: 500
                    }}
                    textStyle={{
                        fontSize: 10,
                        color: "#000000",
                        margin: 0,
                        padding: 0,
                    }}
                    linkStyle={{
                        fontSize: 12,
                        color: "#000000",
                        textDecoration: "underline",
                    }}
                    mdSource={packageSrv.onHireButton.mdContent}
                />
            </Col>
            <Col span={6} style={styles.buttonContainer}>
                <Button style={styles.buttonStyle} onClick={() => setState(1)}>{packageSrv.onHireButton.buttonTitle}</Button>
            </Col>
        </Row>
    );

    const renderStateOne = () => (
        <Row style={{ justifyContent: "center" }}>
            {(packageSrv.onConfirmButton.termsAndConditions) && (
                <>
                    <MarkdownPreviewer
                        h1Style={{
                            fontSize: 15,
                            color: "#000000",
                            fontWeight: 500
                        }}
                        h2Style={{
                            fontSize: 12,
                            color: "#707070",
                            fontWeight: 500
                        }}
                        h3Style={{
                            fontSize: 12,
                            color: "#000000",
                            margin: 0,
                            padding: 0,
                            fontWeight: 500
                        }}
                        h4Style={{
                            fontSize: 12,
                            color: "#000000",
                            margin: 0,
                            padding: 0,
                            fontWeight: 500
                        }}
                        textStyle={{
                            fontSize: 12,
                            color: "#000000",
                            margin: 0,
                            padding: 0,
                        }}
                        linkStyle={{
                            fontSize: 12,
                            color: "#000000",
                            textDecoration: "underline",
                        }}
                        mdSource={packageSrv.onConfirmButton.mdContent}
                    />
                    {
                        loadingHireMegamovil ? (
                            <Loading />
                        ) :
                            (
                                <>
                                    {showCurp && (
                                        <Col span={20} style={{ marginTop: 15, marginBottom: 15 }}>
                                            <Text style={styles.inputTitle}>CURP</Text>
                                            <Input
                                                value={curp}
                                                style={styles.inputText}
                                                onChange={(e) => handleCurpChange(e.target.value)}
                                                maxLength={18}
                                                status={curpError ? "error" : ""}
                                                autoCapitalize="characters"
                                            />
                                            {<Text type="danger" style={styles.emptyFieldError}>{curpError ? curpError : null}</Text>}
                                        </Col>
                                    )}
                                    {showBusinessFields && (
                                        <>
                                            <Col span={20} style={{ marginTop: 15, marginBottom: 5 }}>
                                                <div style={styles.inputContainer}>
                                                    <Text style={styles.inputTitle}>Tipo de persona</Text>
                                                    <Select
                                                        style={styles.inputText}
                                                        placeholder="Selecciona una opción"
                                                        notFoundContent="No se encontraron datos"
                                                        options={businessTypes}
                                                        value={socialReasonType || undefined}
                                                        onChange={(value) => setSocialReasonType(value)}
                                                    />
                                                </div>
                                            </Col>
                                            <Col span={20} style={{ marginTop: 15, marginBottom: 15 }}>
                                                <Text style={styles.inputTitle}>RFC</Text>
                                                <Input
                                                    value={rfc}
                                                    style={styles.inputText}
                                                    onChange={(e) => handleRfcChange(e.target.value)}
                                                    maxLength={13}
                                                    status={rfcError ? "error" : ""}
                                                    autoCapitalize="characters"
                                                />
                                                {<Text type="danger" style={styles.emptyFieldError}>{rfcError ? rfcError : null}</Text>}
                                            </Col>
                                        </>
                                    )}
                                    {
                                        !limitToOne && (
                                            <Col span={20} style={{ marginTop: 15, marginBottom: 15 }}>
                                                <Text style={styles.inputTitle}>Cantidad de {packageSrv.packageType === "INDIVIDUAL" ? "líneas" : "paquetes"}</Text>
                                                <QuantitySelector
                                                    quantity={cantidad}
                                                    setQuantity={setCantidad}
                                                    max={max}
                                                />
                                            </Col>
                                        )
                                    }
                                    {showLineTypeSelector && lineTypeSelections.length > 0 && (
                                        <Col span={20} style={styles.lineTypeSelectorCol}>
                                            <LineTypeSelector
                                                packageType={packageSrv.packageType}
                                                lineTypeSelections={lineTypeSelections}
                                                lineDefinitions={familyLineDefinitions}
                                                onLineTypeChange={handleLineTypeChange}
                                            />
                                        </Col>
                                    )}
                                    <Col span={18} style={styles.mdContainer}>
                                        <Checkbox onChange={() => setChecked(!checked)} checked={checked}>
                                            <p style={{
                                                fontSize: 12,
                                                color: "#000000",
                                                padding: 0,
                                                margin: "10px 0px 10px 0px",
                                            }}>
                                                Aceptar Términos y condiciones
                                            </p>
                                        </Checkbox>
                                    </Col>
                                    <Col span={6} style={styles.buttonContainer}>
                                        <Row>
                                            <Col span={24}>
                                                <Button
                                                    style={checked ? styles.buttonStyle : styles.buttonCancelStyle}
                                                    onClick={handleConfirmPurchase}
                                                    disabled={!checked}
                                                >
                                                    {packageSrv.onConfirmButton.buttonTitle}
                                                </Button>
                                            </Col>
                                            <Col span={24}>
                                                <Button
                                                    style={{ ...styles.buttonCancelStyle, backgroundColor: '#fff', color: '#0055c2', border: '1px solid #0055c2', }}
                                                    onClick={() => setState(0)}
                                                >
                                                    Cancelar
                                                </Button>
                                            </Col>
                                        </Row>
                                    </Col>
                                </>
                            )
                    }
                </>
            )}
        </Row>
    );

    return (
        (state === 0) ? (
            renderStateZero()
        ) : ((state === 1) ? (
            renderStateOne()
        ) : null
        ));
};

const styles = StyleSheet.create({
    mdContainer: {
        paddingRight: 10,
        marginTop: 15
    },
    buttonContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    buttonStyle: {
        height: 30,
        fontSize: 10,
        fontWeight: 600,
        padding: "0px 10px 0px 10px",
        borderRadius: 20,
        border: "none",
        marginBottom: 10,
        marginTop: 10,
        color: "#ffffff",
        backgroundColor: "#0055c2",
    },
    buttonCancelStyle: {
        height: 30,
        fontSize: 10,
        fontWeight: 600,
        padding: "0px 10px 0px 10px",
        borderRadius: 20,
        border: "none",
        marginBottom: 10,
        marginTop: 10,
        color: "#ffffff",
        backgroundColor: "gray",
    },
    emptyFieldError: {
        fontSize: 11
    },
    inputTitle: {
        color: "#0055c2",
        fontWeight: 600,
        fontSize: 12
    },
    inputText: {
        marginTop: 15,
        color: "#000000",
        borderColor: '#000000',
        fontSize: 12
    },
    inputContainer: {
        display: "flex",
        flexDirection: "column",
        marginTop: "10px",
    },
    lineTypeSelectorCol: {
        marginTop: 5,
        marginBottom: 15,
    },
});

//-------------------------------------------------------------------------------------------------- Exports
export default HireMegaMovilServicesComponent;
